import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const kbdVariants = cva("", {
  variants: {
    variant: {
      default: cn(
        "bg-muted text-muted-foreground pointer-events-none inline-flex h-5 w-fit min-w-5 items-center justify-center gap-1 rounded-sm px-1 font-sans text-xs font-medium select-none",
        "[&_svg:not([class*='size-'])]:size-3",
        "[[data-slot=tooltip-content]_&]:bg-background/20 [[data-slot=tooltip-content]_&]:text-background dark:[[data-slot=tooltip-content]_&]:bg-background/10"
      ),
      // A shortcut hint next to a control or a search field.
      hint: "block rounded-sm px-1 font-sans bg-gray-50 [&>span]:text-gray-400",
      // The same hint inside a dark tooltip: an outline, the tooltip's text colour.
      hintOnDark: "block rounded-sm px-1 font-sans border",
    },
  },
  defaultVariants: { variant: "default" },
});

/**
 * A keyboard key or shortcut. `hint` and `hintOnDark` are the small shortcut hints next to
 * controls and inside tooltips; pick the label per platform with `isMac()`.
 *
 * @import import { Kbd } from "@/components/shadcn/kbd";
 */
function Kbd({
  className,
  variant,
  children,
  ...props
}: React.ComponentProps<"kbd"> & VariantProps<typeof kbdVariants>) {
  const hint = variant === "hint" || variant === "hintOnDark";
  return (
    <kbd
      data-slot="kbd"
      className={cn(kbdVariants({ variant }), className)}
      {...props}
    >
      {hint ? <span className="text-xs leading-4">{children}</span> : children}
    </kbd>
  );
}

function KbdGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <kbd
      data-slot="kbd-group"
      className={cn("inline-flex items-center gap-1", className)}
      {...props}
    />
  );
}

export { Kbd, KbdGroup, kbdVariants };
